import { useEffect, useRef, useState } from "react";
import "./App.css";

function App() {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const dancersRef = useRef([]);
  const soundCloudRef = useRef(null);
  const audioRef = useRef(null);
  const particlesRef = useRef([]);
  const particleFrameRef = useRef(null);
  const [countdown, setCountdown] = useState(3);
  const [showAnimation, setShowAnimation] = useState(false);
  const layoutChangeTimerRef = useRef(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [streamMode, setStreamMode] = useState(false); // 'false', 'yes', or 'crafty'
  const [staticDancersLoaded, setStaticDancersLoaded] = useState(false);
  const staticDancerImagesRef = useRef([]);
  const [showFAQ, setShowFAQ] = useState(false);
  const [countdownColor, setCountdownColor] = useState('#FFFFFF');
  
  // Colors for the dancers and particles
  const colors = [
    "#FF5252", // Red
    "#FF7B25", // Orange
    "#FFEB3B", // Yellow
    "#66BB6A", // Green
    "#42A5F5", // Blue
    "#7E57C2", // Purple
    "#EC407A", // Pink
    "#26C6DA", // Teal
  ];

  // Apply stream mode class to body
  useEffect(() => {
    if (streamMode) {
      document.body.classList.add('stream-mode-active');
    } else {
      document.body.classList.remove('stream-mode-active');
    }
    
    return () => {
      document.body.classList.remove('stream-mode-active');
    };
  }, [streamMode]);

  // Load static dancer images
  useEffect(() => {
    // Preload the static dancer images
    const dancerImages = [
      '/dancer1.png',
      '/dancer2.png',
      '/dancer3.png',
      '/dancer4.png'
    ];
    
    let loadedCount = 0;
    
    dancerImages.forEach(src => {
      const img = new Image();
      img.onload = () => {
        staticDancerImagesRef.current.push(img);
        loadedCount++;
        if (loadedCount === dancerImages.length) {
          setStaticDancersLoaded(true);
        }
      };
      img.src = src;
    });
  }, []);

  // Dancer silhouette shapes (SVG paths)
  const dancerShapes = [
    // Dancer with arms up
    {
      path: (ctx) => {
        // Head
        ctx.beginPath();
        ctx.arc(0, -60, 20, 0, Math.PI * 2);
        ctx.fill();
        
        // Body
        ctx.beginPath();
        ctx.moveTo(-15, -40);
        ctx.lineTo(15, -40);
        ctx.lineTo(10, 30);
        ctx.lineTo(-10, 30);
        ctx.closePath();
        ctx.fill();
        
        // Arms up
        ctx.beginPath();
        ctx.moveTo(-15, -30);
        ctx.lineTo(-40, -70);
        ctx.moveTo(15, -30);
        ctx.lineTo(40, -70);
        ctx.lineWidth = 8;
        ctx.stroke();
        
        // Legs apart
        ctx.beginPath();
        ctx.moveTo(-10, 30);
        ctx.lineTo(-30, 80);
        ctx.moveTo(10, 30);
        ctx.lineTo(30, 80);
        ctx.lineWidth = 10;
        ctx.stroke();
      }
    },
    // Dancer in mid-step
    {
      path: (ctx) => {
        // Head
        ctx.beginPath();
        ctx.arc(0, -60, 20, 0, Math.PI * 2);
        ctx.fill();
        
        // Body - slightly curved for movement
        ctx.beginPath();
        ctx.moveTo(-10, -40);
        ctx.quadraticCurveTo(5, -10, 5, 30);
        ctx.lineTo(-15, 30);
        ctx.closePath();
        ctx.fill();
        
        // Arms in motion
        ctx.beginPath();
        ctx.moveTo(-10, -30);
        ctx.lineTo(-40, -10);
        ctx.moveTo(5, -30);
        ctx.lineTo(30, -50);
        ctx.lineWidth = 8;
        ctx.stroke();
        
        // Legs in mid-step
        ctx.beginPath();
        ctx.moveTo(-10, 30);
        ctx.lineTo(-20, 60);
        ctx.lineTo(-30, 90);
        ctx.moveTo(5, 30);
        ctx.lineTo(25, 50);
        ctx.lineWidth = 10;
        ctx.stroke();
      }
    },
    // Dancer with hand on hip
    {
      path: (ctx) => {
        // Head
        ctx.beginPath();
        ctx.arc(0, -60, 20, 0, Math.PI * 2);
        ctx.fill();
        
        // Body
        ctx.beginPath();
        ctx.moveTo(-15, -40);
        ctx.lineTo(15, -40);
        ctx.lineTo(15, 30);
        ctx.lineTo(-15, 30);
        ctx.closePath();
        ctx.fill();
        
        // Arms - one on hip, one out
        ctx.beginPath();
        ctx.moveTo(-15, -30);
        ctx.lineTo(-40, 0);
        ctx.moveTo(15, -30);
        ctx.lineTo(0, 0); // Hand on hip
        ctx.lineWidth = 8;
        ctx.stroke();
        
        // Legs crossed
        ctx.beginPath();
        ctx.moveTo(-15, 30);
        ctx.lineTo(-10, 80);
        ctx.moveTo(15, 30);
        ctx.lineTo(-5, 80);
        ctx.lineWidth = 10;
        ctx.stroke();
      }
    }
  ];

  // Generate a random color
  const getRandomColor = () => {
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Particle class for sparkle effects
  class Particle {
    constructor(x, y, color) {
      this.x = x;
      this.y = y;
      this.color = color || getRandomColor();
      this.size = Math.random() * 5 + 2;
      this.speedX = Math.random() * 8 - 4;
      this.speedY = Math.random() * 8 - 4;
      this.life = 1; // Full life
      this.decay = Math.random() * 0.03 + 0.02; // Random decay rate
    }

    // Update particle position and life
    update() {
      this.x += this.speedX;
      this.y += this.speedY;
      this.speedX *= 0.97; // Slow down over time
      this.speedY *= 0.97;
      this.life -= this.decay; // Decrease life
      
      // Gravity effect
      this.speedY += 0.1;
      
      // Shrink as it dies
      this.size = Math.max(0, this.size * this.life);
    }

    // Draw the particle
    draw(ctx) {
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      
      // Add a glow effect
      ctx.globalAlpha = this.life * 0.5;
      ctx.shadowBlur = 10;
      ctx.shadowColor = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size * 1.5, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    }
  }

  // Create particles at the given position
  const createSparkleExplosion = (x, y) => {
    // Create 30-50 particles
    const numParticles = Math.floor(Math.random() * 20) + 30;
    const color = getRandomColor();
    
    for (let i = 0; i < numParticles; i++) {
      particlesRef.current.push(new Particle(x, y, color));
    }
    
    // Start animation if not already running
    if (!particleFrameRef.current) {
      animateParticles();
    }
  };

  // Animate all particles
  const animateParticles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Only clear the canvas if in countdown mode
    if (!showAnimation) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    
    // Update and draw all particles
    particlesRef.current.forEach((particle, index) => {
      particle.update();
      particle.draw(ctx);
      
      // Remove dead particles
      if (particle.life <= 0) {
        particlesRef.current.splice(index, 1);
      }
    });
    
    // Continue animation if particles exist
    if (particlesRef.current.length > 0) {
      particleFrameRef.current = requestAnimationFrame(animateParticles);
    } else {
      particleFrameRef.current = null;
    }
  };

  // Handle countdown clicks
  const handleCountdownClick = (e) => {
    if (streamMode) return; // Skip in stream mode
    
    // Get click position
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Change countdown color
    const newColor = getRandomColor();
    setCountdownColor(newColor);
    
    // Create sparkle explosion
    createSparkleExplosion(x, y);
    
    // Mark user interaction
    handleUserInteraction();
  };

  // Track user interaction
  const handleUserInteraction = () => {
    if (!userInteracted) {
      setUserInteracted(true);
      
      // Play a silent sound to enable audio context
      const audio = new Audio('/click-sound.mp3');
      audio.volume = 0.1;
      audio.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // Parse URL for stream mode
  useEffect(() => {
    // Check for URL parameters
    const queryParams = new URLSearchParams(window.location.search);
    const streamParam = queryParams.get('stream');
    
    // Check if stream mode is enabled and which one
    if (streamParam === 'yes' || streamParam === 'crafty') {
      setStreamMode(streamParam);
    }
    
    // Setup user interaction listeners
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    
    // Start the countdown
    const countdownInterval = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownInterval);
          setShowAnimation(true);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
    
    return () => {
      clearInterval(countdownInterval);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, []);

  // Play music based on mode
  useEffect(() => {
    if (showAnimation) {
      if (streamMode && audioRef.current) {
        // In stream mode, play the fallback MP3
        setTimeout(() => {
          audioRef.current.play().catch(e => {
            console.log('Fallback audio play failed:', e);
          });
        }, 100);
      } else if (soundCloudRef.current) {
        // Try to auto-play SoundCloud
        setTimeout(() => {
          try {
            const iframe = soundCloudRef.current;
            const iframeWindow = iframe.contentWindow;
            
            // Try to send a play message to the iframe
            if (iframeWindow) {
              iframeWindow.postMessage('play', '*');
            }
          } catch (e) {
            console.log('Error triggering SoundCloud autoplay:', e);
          }
        }, 500);
      }
    }
  }, [showAnimation, streamMode]);

  // Dancer class to manage each dancing figure
  class Dancer {
    constructor(canvas, isOffscreen = false) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.width = canvas.width;
      this.height = canvas.height;
      this.color = colors[Math.floor(Math.random() * colors.length)];
      this.angle = Math.random() > 0.5 ? 90 : 180; // 90 or 180 degrees
      
      // Size now has greater variation - some dancers can be much larger
      this.size = Math.random() * 200 + 100; // Size between 100 and 300
      
      // Position dancers - some can be partially offscreen
      this.reposition(isOffscreen);
      
      // For Bond-style effects
      this.opacity = 1;
      this.fadeDirection = 0; // 0 = no fade, 1 = fade in, -1 = fade out
      this.verticalSlide = 0; // For vertical movement effect
      this.slideSpeed = 0; // Will be set if this is a side dancer
      
      // Decide between programmatic or static image dancers
      this.useStaticImage = staticDancersLoaded && Math.random() < 0.7; // Higher chance to use static images
      
      if (this.useStaticImage && staticDancerImagesRef.current.length > 0) {
        // Use one of the loaded static images
        this.staticImageIndex = Math.floor(Math.random() * staticDancerImagesRef.current.length);
      } else {
        // Use one of the programmatic shapes
        this.shapeIndex = Math.floor(Math.random() * dancerShapes.length);
      }
      
      this.danceMoves = this.generateDanceMoves();
      this.danceStep = 0;
      this.danceSpeed = Math.random() * 0.05 + 0.02;
      this.targetX = this.x;
      this.targetY = this.y;
      this.transitionSpeed = 0.02 + Math.random() * 0.03;
      
      // Special effects for edge dancers
      this.isEdgeDancer = false;
      this.setupSpecialEffects(isOffscreen);
    }
    
    // Setup special effects for large side dancers
    setupSpecialEffects(isOffscreen) {
      if (isOffscreen && this.size > 200) {
        this.isEdgeDancer = true;
        
        // Determine if this is a side dancer (left or right edge)
        const isLeftOrRight = this.x < 0 || this.x > this.width;
        
        // For side dancers, add vertical slide effect with LIMITED SPEED
        if (isLeftOrRight) {
          this.verticalSlide = Math.random() > 0.5 ? 1 : -1; // Direction of slide
          
          // Limit the slide speed to ensure maximum of half window height per animation cycle
          // Calculate a speed that would take at least 4 seconds to traverse half the screen height
          const maxSpeed = this.height / (2 * 240); // Half height over ~4s (240 frames at 60fps)
          this.slideSpeed = Math.random() * maxSpeed * 0.7 + maxSpeed * 0.3; // Between 30-100% of max speed
          
          // Start with fade in for Bond effect
          this.opacity = 0;
          this.fadeDirection = 1; // Fade in
        }
      }
    }

    // Check if a position is in the middle area (for stream mode)
    isInMiddleArea(x, y) {
      if (!streamMode) return false;
      
      // Define the middle 25% of the screen as a rectangle
      const middleWidth = this.width * 0.25;
      const middleHeight = this.height * 0.25;
      const middleX = this.width / 2 - middleWidth / 2;
      const middleY = this.height / 2 - middleHeight / 2;
      
      return (
        x > middleX && 
        x < middleX + middleWidth && 
        y > middleY && 
        y < middleY + middleHeight
      );
    }

    // Reposition the dancer - can be called to change layout
    reposition(forceOffscreen = false) {
      let attempts = 0;
      let validPosition = false;
      
      while (!validPosition && attempts < 10) {
        attempts++;
        
        // Determine if this dancer should be partially offscreen
        const isOffscreen = forceOffscreen || Math.random() < 0.4;
        
        if (isOffscreen) {
          // Position partially offscreen - extend range beyond canvas
          // For x position - can be up to 50% off either edge
          const edge = Math.random() > 0.5 ? -0.5 : 1.5; // Left or right edge
          this.x = this.width * (Math.random() * 0.5 + edge);
          
          // For y position - can be up to 50% off top or bottom
          if (Math.random() > 0.5) {
            this.y = this.height * (Math.random() * 0.5 - 0.5); // Top edge
          } else {
            this.y = this.height * (Math.random() * 0.5 + 1.0); // Bottom edge
          }
          
          // Always valid if offscreen
          validPosition = true;
        } else {
          // Position fully onscreen
          this.x = Math.random() * this.width * 0.8 + this.width * 0.1;
          this.y = Math.random() * this.height * 0.7 + this.height * 0.1;
          
          // Check if in middle area (for stream mode)
          if (streamMode && this.isInMiddleArea(this.x, this.y)) {
            validPosition = false; // Try again if in middle area
          } else {
            validPosition = true;
          }
        }
      }
      
      // If we couldn't find a valid position after 10 attempts, just use the last one
      
      // Set target positions for smooth transitions
      this.targetX = this.x;
      this.targetY = this.y;
    }
    
    // Set a new target position for smooth movement
    setTargetPosition() {
      let attempts = 0;
      let validPosition = false;
      let newX, newY;
      
      while (!validPosition && attempts < 10) {
        attempts++;
        
        const isOffscreen = Math.random() < 0.4;
        
        if (isOffscreen) {
          // Target position partially offscreen
          const edge = Math.random() > 0.5 ? -0.5 : 1.5;
          newX = this.width * (Math.random() * 0.5 + edge);
          
          if (Math.random() > 0.5) {
            newY = this.height * (Math.random() * 0.5 - 0.5);
          } else {
            newY = this.height * (Math.random() * 0.5 + 1.0);
          }
          
          // Always valid if offscreen
          validPosition = true;
        } else {
          // Target position fully onscreen
          newX = Math.random() * this.width * 0.8 + this.width * 0.1;
          newY = Math.random() * this.height * 0.7 + this.height * 0.1;
          
          // Check if in middle area (for stream mode)
          if (streamMode && this.isInMiddleArea(newX, newY)) {
            validPosition = false; // Try again if in middle area
          } else {
            validPosition = true;
          }
        }
      }
      
      // If we couldn't find a valid position after 10 attempts, just use the last calculated one
      this.targetX = newX;
      this.targetY = newY;
      
      // Randomize the transition speed a bit
      this.transitionSpeed = 0.01 + Math.random() * 0.02;
      
      // For edge dancers with special effects, apply fade
      if (this.isEdgeDancer) {
        // Reset fade for new position
        this.opacity = 0;
        this.fadeDirection = 1; // Start fading in
      }
    }

    // Generate dance movement patterns
    generateDanceMoves() {
      const numMoves = 20 + Math.floor(Math.random() * 10);
      const moves = [];
      
      for (let i = 0; i < numMoves; i++) {
        moves.push({
          armAngle: Math.random() * Math.PI,
          legAngle: Math.random() * Math.PI * 0.5,
          bodyTilt: (Math.random() - 0.5) * 0.3,
          headBob: Math.random() * 0.2
        });
      }
      
      return moves;
    }

    // Draw the dancer
    draw() {
      const ctx = this.ctx;
      ctx.save();
      
      // Apply rotation based on angle
      ctx.translate(this.x, this.y);
      ctx.rotate((this.angle * Math.PI) / 180);
      
      // Apply opacity
      const opacityHex = Math.floor(this.opacity * 204).toString(16).padStart(2, '0');
      
      if (this.useStaticImage && staticDancerImagesRef.current.length > 0) {
        // Draw the static image dancer
        const img = staticDancerImagesRef.current[this.staticImageIndex];
        
        // Set global composite operation to color-in the white parts with our color
        ctx.globalCompositeOperation = 'source-over';
        
        // Scale appropriate to the image
        const scale = this.size / 200;
        ctx.scale(scale, scale);
        
        // Calculate image dimensions to center it
        const width = img.width || 200;
        const height = img.height || 400;
        
        // Create colored version by drawing to an offscreen canvas first
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = width;
        tempCanvas.height = height;
        
        // Fill with color
        tempCtx.fillStyle = this.color + opacityHex;
        tempCtx.fillRect(0, 0, width, height);
        
        // Use the image as a mask
        tempCtx.globalCompositeOperation = 'destination-in';
        tempCtx.drawImage(img, 0, 0);
        
        // Draw the resulting colored image to main canvas
        ctx.drawImage(tempCanvas, -width/2, -height/2, width, height);
      } else {
        // Draw programmatic dancer
        ctx.scale(this.size / 150, this.size / 150);
        
        const move = this.danceMoves[Math.floor(this.danceStep)];
        
        // Set color with appropriate opacity
        ctx.fillStyle = this.color + opacityHex;
        ctx.strokeStyle = this.color + opacityHex;
        
        // Apply body tilt animation
        ctx.rotate(move.bodyTilt);
        
        // Draw dancer using the selected shape
        dancerShapes[this.shapeIndex].path(ctx);
      }
      
      ctx.restore();
    }

    // Update the dance animation
    update() {
      // Update dance animation
      this.danceStep = (this.danceStep + this.danceSpeed) % this.danceMoves.length;
      
      // Move smoothly toward target position
      this.x += (this.targetX - this.x) * this.transitionSpeed;
      this.y += (this.targetY - this.y) * this.transitionSpeed;
      
      // Special effects for edge dancers
      if (this.isEdgeDancer) {
        // Handle fade in/out effect
        if (this.fadeDirection === 1) {
          this.opacity += 0.01;
          if (this.opacity >= 1) {
            this.opacity = 1;
            // After fully faded in, start a countdown to fade out
            if (Math.random() < 0.005) {
              this.fadeDirection = -1;
            }
          }
        } else if (this.fadeDirection === -1) {
          this.opacity -= 0.01;
          if (this.opacity <= 0) {
            this.opacity = 0;
            // After fully faded out, reposition and prepare to fade in
            this.reposition(true);
            this.fadeDirection = 1;
          }
        }
        
        // Vertical slide effect for side dancers
        if (this.verticalSlide !== 0 && (this.x < 0 || this.x > this.width)) {
          this.y += this.verticalSlide * this.slideSpeed;
          
          // If moved too far, reset position
          if ((this.verticalSlide > 0 && this.y > this.height + this.size) || 
              (this.verticalSlide < 0 && this.y < 0 - this.size)) {
            // Reset to the opposite side
            this.y = this.verticalSlide > 0 ? -this.size : this.height + this.size;
            
            // Occasionally change color when resetting
            if (Math.random() < 0.3) {
              this.color = colors[Math.floor(Math.random() * colors.length)];
              if (!this.useStaticImage) {
                this.shapeIndex = Math.floor(Math.random() * dancerShapes.length);
              } else if (staticDancerImagesRef.current.length > 1) {
                this.staticImageIndex = Math.floor(Math.random() * staticDancerImagesRef.current.length);
              }
            }
          }
        }
      }
      
      // Occasionally change color
      if (Math.random() < 0.002) {
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
    }
  }

  // Change layout of all dancers
  const changeLayout = () => {
    if (dancersRef.current.length === 0) return;
    
    // Set new target positions for all dancers
    dancersRef.current.forEach(dancer => {
      dancer.setTargetPosition();
    });
    
    // Schedule next layout change - between 3 and 6 seconds
    const nextChangeTime = 3000 + Math.random() * 3000;
    layoutChangeTimerRef.current = setTimeout(changeLayout, nextChangeTime);
  };

  // Initialize the canvas and dancers
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas dimensions to fill the window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create dancers
    const numDancers = 8; // Increased number of dancers
    dancersRef.current = [];
    
    // Create mix of regular and offscreen dancers
    for (let i = 0; i < numDancers; i++) {
      // Make about 40% of dancers start partially offscreen
      const isOffscreen = i < Math.floor(numDancers * 0.4);
      dancersRef.current.push(new Dancer(canvas, isOffscreen));
    }

    // Start animation
    animateCanvas();
    
    // Start layout changes
    layoutChangeTimerRef.current = setTimeout(changeLayout, 3000); // First change after 3 seconds
  };

  // Animation loop
  const animateCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Clear canvas - always just use clearRect for transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Only fill black in regular mode
    if (!streamMode) {
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // Draw and update all dancers
    dancersRef.current.forEach(dancer => {
      dancer.draw();
      dancer.update();
    });
    
    // Continue animation loop
    animationFrameRef.current = requestAnimationFrame(animateCanvas);
  };

  // Handle window resize
  const handleResize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };

  // Set up canvas and event listeners when animation should start
  useEffect(() => {
    if (showAnimation) {
      initCanvas();
      window.addEventListener('resize', handleResize);
      
      // Clean up on unmount
      return () => {
        window.removeEventListener('resize', handleResize);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (layoutChangeTimerRef.current) {
          clearTimeout(layoutChangeTimerRef.current);
        }
      };
    }
  }, [showAnimation]);

  // Setup canvas even during countdown for particle effects
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
  }, []);

  // FAQ Component
  const FAQModal = () => {
    if (!showFAQ) return null;
    
    return (
      <div className="faq-overlay" onClick={() => setShowFAQ(false)}>
        <div className="faq-modal" onClick={(e) => e.stopPropagation()}>
          <button className="faq-close" onClick={() => setShowFAQ(false)}>×</button>
          <h2>FAQ</h2>
          <div className="faq-item">
            <h3>Q: What?</h3>
            <p>A: It's shimmy time - a shimmy a day keeps the sadness away?</p>
          </div>
          <div className="faq-item">
            <h3>Q: Really?</h3>
            <p>A: Yeah - just try it, even if you feel silly!</p>
          </div>
          <div className="faq-item">
            <h3>Q: Where?</h3>
            <p>A: <a href="https://www.tiktok.com/@beefedlump/video/7490219582645439787" target="_blank" rel="noopener noreferrer">This tiktok</a> was originally posted by beef lump</p>
          </div>
          <div className="faq-item">
            <h3>Q: Song?</h3>
            <p>A: The song is "There She Goes" - CYRIL x Moonlight x The La's</p>
            <div className="music-links">
              <a href="https://open.spotify.com/track/1ouHvQBpjxROQp0byBbyKN" target="_blank" rel="noopener noreferrer">Spotify</a> | 
              <a href="https://music.youtube.com/watch?v=UNE9Fz_KrNw&si=WAbjtmFKyJMfWjpn" target="_blank" rel="noopener noreferrer">Youtube music</a> | 
              <a href="https://music.apple.com/tr/album/there-she-goes-single/1814189285" target="_blank" rel="noopener noreferrer">Apple music</a> | 
              <a href="https://soundcloud.com/cyrilriley/there-she-goes" target="_blank" rel="noopener noreferrer">Soundcloud</a>
            </div>
          </div>
          <div className="faq-item creator-info">
            <p>This page was made by Twitch streamer <a href="https://twitch.tv/CraftingChaosGaming" target="_blank" rel="noopener noreferrer">CraftingChaosGaming</a> - we hope it helps you shimmy your sadness away too!</p>
          </div>
        </div>
      </div>
    );
  };

  // Determine if we should show the main title
  const showMainTitle = !streamMode || (streamMode !== 'crafty');

  return (
    <div className={`App ${streamMode ? 'stream-mode' : ''}`} onClick={handleUserInteraction}>
      {!showAnimation && (
        <div className="countdown-container" onClick={!streamMode ? handleCountdownClick : undefined}>
          <div className="countdown-wrapper">
            <div className="countdown" style={{ color: countdownColor }}>{countdown}</div>
            {!streamMode && <div className="countdown-message">Quickly - click here!</div>}
          </div>
        </div>
      )}
      
      {showAnimation && (
        <>
          {/* Only show title in regular mode or stream=yes mode (not crafty) */}
          {showMainTitle && (
            <div className="shimmy-title">
              <span className="shimmy-text">It's Shimmy Time</span>
            </div>
          )}
          
          {/* Show shimmy.gif in crafty mode */}
          {streamMode === 'crafty' && showAnimation && (
            <div className="shimmy-gif-container">
              <img 
                src="/shimmy.gif" 
                alt="Shimmy" 
                className="shimmy-gif"
                onError={(e) => console.error("Error loading shimmy.gif:", e)}
              />
            </div>
          )}
          
          {/* Only show in non-stream mode */}
          {!streamMode && (
            <div className="site-credit">
              dailyshimmy.com
            </div>
          )}
        </>
      )}
      
      <canvas 
        ref={canvasRef} 
        className="canvas-layer"
      />
      
      {/* Audio/Music Elements - Different options based on mode */}
      {showAnimation && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-10 w-80 md:w-96">
          {/* Fallback MP3 for stream mode */}
          {streamMode && (
            <audio 
              ref={audioRef}
              src="/shimmy-time.mp3"
              autoPlay
              style={{ display: 'none' }}
            />
          )}
          
          {/* SoundCloud Widget for normal mode */}
          {!streamMode && (
            <iframe
              ref={soundCloudRef}
              width="100%" 
              height="166"
              scrolling="no" 
              frameBorder="no"
              allow="autoplay"
              src="https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/cyrilriley/there-she-goes&t=15s&auto_play=true&show_comments=true&show_user=true&show_reposts=false&visual=true"
              title="SoundCloud Player"
            />
          )}
        </div>
      )}
      
      {/* FAQ Button - Only in regular mode */}
      {showAnimation && !streamMode && (
        <button className="faq-button" onClick={() => setShowFAQ(true)}>
          FAQ
        </button>
      )}
      
      {/* FAQ Modal */}
      {!streamMode && <FAQModal />}
      
      {/* Hidden button that gets virtually clicked to start audio */}
      {showAnimation && userInteracted && (
        <button 
          id="autoplay-button" 
          style={{ display: 'none' }}
          onClick={() => {
            if (soundCloudRef.current) {
              soundCloudRef.current.contentWindow.postMessage('play', '*');
            }
          }}
        >
          Play
        </button>
      )}
    </div>
  );
}

export default App;